import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Heart, Award, Book, Globe, ArrowRight, Star, Shield, Gem, Lock, Zap } from 'lucide-react';
import { usePageTitle } from '../hooks/usePageTitle';
import PulseTimeline from '../components/PulseTimeline';

const ROMONA_PHOTO = 'https://customer-assets.emergentagent.com/job_12c5979f-07d4-4fbe-88fc-c2d666777007/artifacts/3xo7hul1_Facetune_16-11-2024-19-08-45.jpeg';
const BOOK_COVER = 'https://customer-assets.emergentagent.com/job_bf3299c0-3855-4a52-beac-c89141d44eb4/artifacts/cjp88sz1_May%2020%2C%202025%20at%2002_18_35%20PM.png';
const TIMELINE_CHART = 'https://customer-assets.emergentagent.com/job_c1fa4e5b-6f24-4847-890a-fbba66f17abd/artifacts/y3e58g9d_Annotated_Pulse_Timeline_Chart.png';

const RomonaPage = () => {
  usePageTitle('Romona Nicole Schmidt - Gemstone Healing Expert');

  return (
    <div className="min-h-screen bg-[#faf8f5]">
      {/* Hero */}
      <section className="relative py-24 px-6 bg-gradient-to-br from-[#0a0a20] via-[#1a0a2e] to-[#0a0a20] overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-1/4 w-80 h-80 bg-[#9D4EDD]/40 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#c9a96e]/20 rounded-full blur-3xl" />
        </div>
        {[...Array(15)].map((_, i) => (
          <div key={i} className="absolute w-0.5 h-0.5 bg-white rounded-full animate-pulse" style={{ top: `${Math.random() * 100}%`, left: `${Math.random() * 100}%`, animationDelay: `${i * 0.3}s`, opacity: 0.3 + Math.random() * 0.7 }} />
        ))}

        <div className="max-w-6xl mx-auto relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="text-center md:text-left">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 mb-6">
                <Shield className="w-4 h-4 text-[#c9a96e]" />
                <span className="text-sm text-white/80 tracking-widest uppercase">Sacred Practitioner</span>
              </div>
              <h1 className="font-serif text-4xl md:text-5xl text-white mb-3 leading-tight" data-testid="romona-title">
                Romona Nicole <span className="text-[#c9a96e]">Schmidt</span>
              </h1>
              <p className="text-xl text-[#c9a96e] mb-4 font-medium">Entrepreneur &middot; Metaphysical Author &middot; Digital Strategist</p>
              <p className="text-white/60 leading-relaxed max-w-lg mb-8">
                A visionary entrepreneur with over 22 years of expertise in digital strategy and brand development,
                bridging ancient crystal wisdom with modern innovation. Creator of The Pulse Theory of Civilizational Transformation.
              </p>
              <div className="flex flex-wrap gap-4 justify-center md:justify-start">
                <Link to="/book" className="px-6 py-3 bg-gradient-to-r from-[#c9a96e] to-[#FFB800] text-[#1e1919] rounded-xl font-bold hover:shadow-xl transition-all" data-testid="romona-book-link">
                  <Book className="w-4 h-4 inline mr-2" />Get the Book
                </Link>
                <Link to="/webinar-registration" className="px-6 py-3 bg-white/10 border border-white/20 text-white rounded-xl font-semibold hover:bg-white/20 transition-all" data-testid="romona-webinar-link">
                  <Sparkles className="w-4 h-4 inline mr-2" />Join the Webinar
                </Link>
              </div>
            </div>
            <div className="flex justify-center">
              <div className="relative group">
                <div className="absolute -inset-4 bg-gradient-to-br from-[#9D4EDD]/30 to-[#c9a96e]/20 rounded-full blur-2xl group-hover:blur-3xl transition-all duration-500" />
                <img src={ROMONA_PHOTO} alt="Romona Nicole Schmidt" className="relative w-64 h-64 md:w-80 md:h-80 rounded-full object-cover shadow-2xl border-4 border-[#c9a96e]/30" data-testid="romona-photo" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sacred Intention Section - Protective energy content */}
      <section className="py-16 px-6 bg-gradient-to-b from-[#0a0a20] to-[#faf8f5]">
        <div className="max-w-3xl mx-auto text-center">
          <Shield className="w-10 h-10 text-[#9D4EDD] mx-auto mb-4" />
          <h2 className="font-serif text-2xl text-[#1e1919] mb-6">A Sacred Space of Intention</h2>
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-[#9D4EDD]/20">
            <p className="text-[#4A4A6A] leading-relaxed mb-4">
              This work is rooted in <strong>integrity, light, and divine purpose</strong>. Every crystal teaching,
              every healing session, and every word written by Romona is infused with the protective energy of
              Black Tourmaline, the clarity of Selenite, and the unconditional love of Rose Quartz.
            </p>
            <p className="text-[#4A4A6A] leading-relaxed mb-4">
              We honor the ancient law of resonance: <em>only those vibrating at the frequency of genuine seeking,
              honest intention, and respect for sacred knowledge will find alignment here.</em> This is not a space
              for ego, manipulation, or ill will. The crystals themselves hold discernment. They recognize the
              hearts that approach with purity and those that do not.
            </p>
            <p className="text-[#61525a] leading-relaxed text-sm italic">
              "Energy precedes form. What you carry within you determines what you receive.
              Those who come seeking light shall find it magnified. Those who carry darkness
              will find only mirrors." — Romona Nicole Schmidt
            </p>
          </div>
        </div>
      </section>

      {/* Featured: The Book */}
      <section className="py-16 px-6" data-testid="romona-book-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#c9a96e]/10 text-[#c9a96e] text-sm font-semibold mb-4">
              <Book className="w-4 h-4" />
              New Release
            </div>
            <h2 className="font-serif text-3xl text-[#1e1919]">The Book</h2>
          </div>

          <div className="bg-gradient-to-r from-[#1e1919] to-[#2a2030] rounded-2xl p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-10 items-center">
              <div className="flex justify-center">
                <img src={BOOK_COVER} alt="Crystals of the Pulse" className="w-56 md:w-64 rounded-lg shadow-2xl hover:scale-[1.02] transition-transform duration-500" />
              </div>
              <div>
                <h3 className="font-serif text-3xl text-white mb-2">Crystals of the Pulse</h3>
                <p className="text-[#c9a96e] mb-4 italic">Earth's Wisdom for Shifting Times</p>
                <p className="text-white/70 leading-relaxed mb-6">
                  A groundbreaking exploration of the ancient intelligence of Earth's crystals and their role
                  in transformative periods. Through the synthesis of ancient wisdom, metaphysical insights,
                  geological data, and AI-powered pattern recognition, Romona unveils the hidden rhythm
                  connecting crystals, civilizations, and your own evolution.
                </p>
                <ul className="space-y-2 mb-6">
                  {['10 chapters across 3 immersive parts', 'The Pulse Timeline framework', 'Crystal rituals for the modern shift', 'Children of the Pulse guidance'].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-white/80 text-sm">
                      <Star className="w-3 h-3 text-[#FFB800] flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
                <div className="flex items-center gap-4 mb-4">
                  <span className="font-serif text-3xl text-[#c9a96e]">$85.00</span>
                  <span className="px-3 py-1 rounded-full bg-[#00C853]/20 text-[#00C853] text-xs font-bold uppercase animate-pulse">
                    <Gem className="w-3 h-3 inline mr-1" />Free Crystal Included
                  </span>
                </div>
                <Link to="/book" className="inline-flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-[#c9a96e] to-[#FFB800] text-[#1e1919] rounded-xl font-bold hover:shadow-xl transition-all">
                  Order Your Copy <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured: The Webinar */}
      <section className="py-16 px-6 bg-gradient-to-r from-[#f8f5ff] to-[#fff5f8]" data-testid="romona-webinar-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#9D4EDD]/10 text-[#9D4EDD] text-sm font-semibold mb-4">
              <Sparkles className="w-4 h-4" />
              Live Event
            </div>
            <h2 className="font-serif text-3xl text-[#1e1919]">The Webinar</h2>
          </div>

          <div className="bg-white rounded-2xl shadow-xl border border-[#e8e4de] overflow-hidden">
            <div className="bg-gradient-to-r from-[#9D4EDD] to-[#FF6B9D] p-6 text-center">
              <h3 className="font-serif text-2xl text-white mb-1">Selenite Healing Mastery</h3>
              <p className="text-white/80">A Live Intensive with Romona Nicole Schmidt</p>
            </div>
            <div className="p-8 md:p-10">
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                {[
                  { label: 'Date', value: 'July 2026', icon: '📅' },
                  { label: 'Duration', value: '3 Hours Live', icon: '⏱' },
                  { label: 'Includes', value: 'Selenite Kit', icon: '📦' },
                ].map((item, i) => (
                  <div key={i} className="text-center p-4 bg-[#f8f5ff] rounded-xl">
                    <span className="text-2xl block mb-1">{item.icon}</span>
                    <p className="text-xs text-[#7A7A9A] uppercase tracking-wider">{item.label}</p>
                    <p className="font-semibold text-[#1e1919]">{item.value}</p>
                  </div>
                ))}
              </div>
              <p className="text-[#4A4A6A] leading-relaxed mb-6 text-center max-w-2xl mx-auto">
                Unlock the transformative power of Selenite — the crystal of angelic connection and
                spiritual cleansing. Learn advanced techniques for energy clearing, aura restoration,
                and space purification directly from Romona. This exclusive session is limited to
                serious practitioners who are ready to elevate their healing practice.
              </p>
              <div className="flex items-center justify-center gap-6">
                <span className="font-serif text-3xl text-[#9D4EDD]">$800</span>
                <Link to="/webinar-registration" className="px-8 py-3 bg-gradient-to-r from-[#9D4EDD] to-[#FF6B9D] text-white rounded-xl font-bold hover:shadow-xl hover:shadow-[#9D4EDD]/30 transition-all">
                  Reserve Your Seat <ArrowRight className="w-4 h-4 inline ml-1" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Second Book - The Pulse Theory */}
      <section className="py-16 px-6" data-testid="romona-pulse-theory-section">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-[#1e1919] via-[#2a2030] to-[#1e1919] rounded-2xl p-8 md:p-10 border border-[#9D4EDD]/20 shadow-xl">
            <div className="flex items-center gap-2 mb-6">
              <span className="px-3 py-1 rounded-full bg-[#FFB800]/20 text-[#FFB800] text-xs font-bold uppercase tracking-wider">Coming Soon</span>
              <span className="px-3 py-1 rounded-full bg-white/10 text-white/60 text-xs font-bold uppercase tracking-wider">Volume II</span>
            </div>
            <h3 className="font-serif text-3xl text-white mb-2 leading-tight">
              The Pulse Theory of <span className="text-[#c9a96e]">Civilizational Transformation</span>
            </h3>
            <p className="text-white/50 mb-6 italic">by Nicole Schmidt &middot; 18 Chapters &middot; Pattern recognition assisted by AI</p>
            <p className="text-white/70 leading-relaxed mb-6 max-w-2xl">
              Is there a hidden rhythm behind the rise and fall of civilizations? This pioneering work unveils 500-year
              cycles of Collapse, Revelation, Rebirth, and Overreach — a Pulse that moves through history as both
              a cosmic pattern and a personal invitation. From gemstones and galactic intelligence to wealth,
              intuition, and the role of the empath, this is a map for navigating the shift we are living through now.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              {['The Four Rhythms', 'Cosmic Intelligence', 'Gemstones & the Pulse', 'Pulse Wealth & Innovation'].map((topic, i) => (
                <div key={i} className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-center">
                  <p className="text-white/80 text-xs font-semibold">{topic}</p>
                </div>
              ))}
            </div>
            <p className="text-[#c9a96e] text-sm font-semibold italic">
              "To those who sense the rhythm but were told it was just a phase — it is. It's a Pulse."
            </p>
          </div>
        </div>
      </section>

      {/* Interactive Pulse Timeline */}
      <section className="py-16 px-6 bg-gradient-to-r from-[#f8f5ff] to-[#faf8f5]" data-testid="romona-timeline-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#c9a96e]/10 text-[#c9a96e] text-sm font-semibold mb-4">
              <Zap className="w-4 h-4" />
              Interactive Research
            </div>
            <h2 className="font-serif text-3xl text-[#1e1919] mb-2">The Pulse Timeline</h2>
            <p className="text-[#61525a] max-w-xl mx-auto">
              Explore the 500-year civilizational shifts Nicole's research has uncovered — and the crystals that resonated through each era.
            </p>
          </div>
          <PulseTimeline />
          <div className="mt-8 flex justify-center">
            <img
              src={TIMELINE_CHART}
              alt="The Pulse Timeline: Civilizational Shifts and Crystal Resonance"
              className="max-w-full md:max-w-2xl rounded-xl shadow-lg border border-[#e8e4de]"
            />
          </div>
        </div>
      </section>

      {/* About / Bio */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-serif text-3xl text-[#1e1919] text-center mb-10">About Nicole</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <p className="text-[#4A4A6A] leading-relaxed mb-4">
                Romona Nicole Schmidt is a dynamic entrepreneur, creative visionary, and digital strategist
                with over 22 years of expertise in online visibility, brand development, and lead generation.
                As the founder behind multiple successful ventures spanning luxury, wellness, and service industries,
                Nicole blends innovation with spiritual depth, business acumen, and heartfelt intention.
              </p>
              <p className="text-[#4A4A6A] leading-relaxed mb-4">
                Her diverse heritage — rooted in Cherokee, Irish, and Black and Brown American traditions — shines
                through in her multifaceted approach to life and business. Nicole is a metaphysical researcher
                who integrates gemstones, wellness rituals, and energetic alignment into her creative projects
                and daily spaces, bringing intentionality to everything she builds.
              </p>
              <p className="text-[#4A4A6A] leading-relaxed">
                She is currently developing <em className="text-[#9D4EDD] font-medium">The Pulse Theory of Civilizational Transformation</em> and
                writing its companion volume <em className="text-[#9D4EDD] font-medium">Crystals of the Pulse</em>, blending historical cycles
                with spiritual insight and AI-powered pattern recognition. A devoted mother nurturing the next
                generation of conscious thinkers, Nicole brings bold vision, grace, and resilience to every space she enters.
              </p>
            </div>
            <div className="space-y-4">
              {[
                { icon: Zap, label: '22+ Years Digital Expertise', desc: 'SEO, brand development, lead generation, and online visibility strategy' },
                { icon: Book, label: 'Published Author', desc: 'Crystals of the Pulse & The Pulse Theory of Civilizational Transformation' },
                { icon: Globe, label: 'Serial Entrepreneur', desc: 'Founder of multiple successful ventures in luxury, wellness, and service industries' },
                { icon: Gem, label: 'Metaphysical Researcher', desc: 'Pioneer integrating gemstone intelligence with historical cycle analysis' },
                { icon: Heart, label: 'Devoted Mother', desc: 'Nurturing the next generation of conscious, purpose-driven thinkers' },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3 p-3 bg-white rounded-xl border border-[#e8e4de]">
                  <div className="w-10 h-10 rounded-lg bg-[#9D4EDD]/10 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-5 h-5 text-[#9D4EDD]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm text-[#1e1919]">{item.label}</h4>
                    <p className="text-xs text-[#7A7A9A]">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Energetic Boundaries - More protective content */}
      <section className="py-16 px-6 bg-[#0a0a20]">
        <div className="max-w-3xl mx-auto text-center">
          <Lock className="w-8 h-8 text-[#c9a96e] mx-auto mb-4" />
          <h2 className="font-serif text-2xl text-white mb-6">Energetic Boundaries</h2>
          <p className="text-white/60 leading-relaxed mb-6">
            The teachings shared through this work carry the vibrational signature of their creator.
            They are encoded with protective frequencies that respond to the energy of the reader.
            Like a crystal that only activates for the right holder, this knowledge reveals its
            deepest truths to those who approach with <strong className="text-[#c9a96e]">humility, sincerity, and pure intention</strong>.
          </p>
          <div className="grid grid-cols-3 gap-4 mt-8">
            {[
              { stone: 'Black Tourmaline', purpose: 'Protection', color: '#4A4A6A' },
              { stone: 'Selenite', purpose: 'Purification', color: '#e0d8cc' },
              { stone: 'Amethyst', purpose: 'Discernment', color: '#9D4EDD' },
            ].map((item, i) => (
              <div key={i} className="p-4 rounded-xl bg-white/5 border border-white/10">
                <div className="w-8 h-8 rounded-full mx-auto mb-2" style={{ background: item.color, opacity: 0.6 }} />
                <p className="text-white/80 text-sm font-semibold">{item.stone}</p>
                <p className="text-white/40 text-xs">{item.purpose}</p>
              </div>
            ))}
          </div>
          <p className="text-white/30 text-xs mt-8 italic">
            "Only light can enter where light resides. The shadows dissolve upon arrival."
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="font-serif text-3xl text-[#1e1919] mb-4">Begin Your Sacred Journey</h2>
          <p className="text-[#61525a] mb-8">
            Whether through the written word or a live experience, Romona's teachings
            are waiting for those who are ready to receive them.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/book" className="px-8 py-4 bg-gradient-to-r from-[#c9a96e] to-[#FFB800] text-[#1e1919] rounded-xl font-bold hover:shadow-xl transition-all">
              <Book className="w-4 h-4 inline mr-2" />Order the Book — $85
            </Link>
            <Link to="/webinar-registration" className="px-8 py-4 bg-gradient-to-r from-[#9D4EDD] to-[#FF6B9D] text-white rounded-xl font-bold hover:shadow-xl transition-all">
              <Sparkles className="w-4 h-4 inline mr-2" />Join the Webinar — $800
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RomonaPage;
